import { ReactTable } from "@aix/ui"
import { useSearch } from "@tanstack/react-router"
import { CheckCircle2, Clock, Eye, ThumbsDown, ThumbsUp, XCircle } from "lucide-react"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"

export default function ExtractedDataTab() {
  const search = useSearch({ strict: false }) as { jobId?: string }
  const [selectedJobId, setSelectedJobId] = useState<string>("")
  const [selectedStatus, setSelectedStatus] = useState<string>("")
  const [minConfidence, setMinConfidence] = useState<number>(0)
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  const { data: extractionJobs } = trpc.extraction.listExtractionJobs.useQuery({})
  const { data, isLoading, refetch } = trpc.extraction.listExtractedData.useQuery({
    extractionJobId: selectedJobId || undefined,
    status: selectedStatus || undefined,
    minConfidence: minConfidence || undefined,
    limit: 100,
  })
  const { mutateAsync: updateDataStatus } = trpc.extraction.updateDataStatus.useMutation()

  // Set job filter from URL params
  useEffect(() => {
    if (search.jobId && search.jobId !== selectedJobId) {
      setSelectedJobId(search.jobId)
    }
  }, [search.jobId])

  const handleStatusUpdate = async (status: "approved" | "rejected", rejectReason?: string) => {
    if (selectedItems.length === 0) {
      toast.error("Please select items to update")
      return
    }

    try {
      await updateDataStatus({
        dataIds: selectedItems,
        status,
        rejectReason,
        reviewedBy: "user", // Could be current user ID
      })
      toast.success(`${selectedItems.length} items ${status}`)
      setSelectedItems([])
      void refetch()
    } catch (error) {
      toast.error(`Failed to ${status} items`)
      console.error(error)
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems((data || []).map((item: any) => item.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-primary">Loading extracted data...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Extracted Data</h2>
        <p className="text-sm text-primary">Review and manage AI-extracted data from your scraping results</p>
      </div>

      {/* Filters */}
      <div className="bg-foregroundx p-4 rounded-lg space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Extraction Job</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
            >
              <option value="">All jobs</option>
              {extractionJobs?.map((job: any) => (
                <option key={job.id} value={job.id}>
                  {job.templateId} - {job.id.slice(0, 8)}... ({new Date(job.createdAt).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Status</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="processed">Processed</option>
              <option value="synced">Synced</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Min Confidence</label>
            <input
              type="number"
              min="0"
              max="100"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-24 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={minConfidence}
              onChange={(e) => setMinConfidence(parseInt(e.target.value) || 0)}
            />
          </div>
          <button
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-background text-primary  transition-colors"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div className="flex items-center gap-4 pt-4 border-t border-border">
            <span className="text-sm text-primary">{selectedItems.length} items selected</span>
            <div className="flex gap-2">
              <button
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                onClick={() => handleStatusUpdate("approved")}
              >
                <ThumbsUp className="w-4 h-4 mr-1" />
                Approve
              </button>
              <button
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                onClick={() => {
                  const reason = prompt("Reason for rejection (optional):")
                  void handleStatusUpdate("rejected", reason || undefined)
                }}
              >
                <ThumbsDown className="w-4 h-4 mr-1" />
                Reject
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <ReactTable
          data={(data || []).map((item: any) => {
            let extractedContent = {}
            try {
              extractedContent = JSON.parse(item.extractedContent || "{}")
            } catch (e) {
              // Use empty object if parsing fails
            }

            return {
              id: item.id,
              selected: selectedItems.includes(item.id),
              templateId: item.templateId,
              templateVersion: item.templateVersion,
              llmModel: item.llmModel,
              confidence: item.confidence || 0,
              status: item.status,
              extractedContent,
              createdAt: new Date(item.createdAt).toLocaleString(),
              reviewedAt: item.reviewedAt ? new Date(item.reviewedAt).toLocaleString() : "-",
              reviewedBy: item.reviewedBy || "-",
              rejectReason: item.rejectReason,
            }
          })}
          columns={[
            {
              Header: "Select",
              accessor: "selected",
              Cell: ({ row: { original } }) => (
                <input
                  type="checkbox"
                  checked={original.selected}
                  onChange={(e) => handleSelectItem(original.id, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              ),
            },
            {
              Header: "ID",
              accessor: "id",
              Cell: ({ row: { original } }) => <div className="font-mono text-xs ">{original.id.slice(0, 8)}...</div>,
            },
            {
              Header: "Template",
              accessor: "templateId",
              Cell: ({ row: { original } }) => (
                <div className="text-sm">
                  <div className=" font-medium">{original.templateId}</div>
                  <div className="text-xs text-gray-500">{original.llmModel}</div>
                </div>
              ),
            },
            {
              Header: "Confidence",
              accessor: "confidence",
              Cell: ({ row: { original } }) => {
                const confidence = original.confidence
                const color =
                  confidence >= 80 ? "text-green-600" : confidence >= 60 ? "text-yellow-600" : "text-destructive"
                return <div className={`text-sm font-medium ${color}`}>{confidence}%</div>
              },
            },
            {
              Header: "Content Preview",
              accessor: "extractedContent",
              Cell: ({ row: { original } }) => {
                const content = original.extractedContent
                let preview = "No content"

                try {
                  if (content.prompts && Array.isArray(content.prompts)) {
                    preview = content.prompts
                      .slice(0, 2)
                      .map((p: any) => p.text?.substring(0, 50) + "...")
                      .join("; ")
                  } else if (content.articles && Array.isArray(content.articles)) {
                    preview = content.articles
                      .slice(0, 2)
                      .map((a: any) => a.headline?.substring(0, 50) + "...")
                      .join("; ")
                  } else if (typeof content === "string") {
                    preview = content.substring(0, 100) + "..."
                  } else {
                    preview = JSON.stringify(content).substring(0, 100) + "..."
                  }
                } catch (e) {
                  preview = "Unable to preview content"
                }

                return (
                  <div className="group relative max-w-xs">
                    <div className="text-sm  truncate cursor-help">{preview}</div>
                    <div className="absolute left-0 top-full mt-2 w-96 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                      <div className="font-semibold mb-2">Full Content:</div>
                      <pre className="text-xs whitespace-pre-wrap break-words max-h-64 overflow-auto">
                        {JSON.stringify(content, null, 2)}
                      </pre>
                    </div>
                  </div>
                )
              },
            },
            {
              Header: "Status",
              accessor: "status",
              Cell: ({ row: { original } }) => {
                const statusIcons = {
                  pending: <Clock className="w-4 h-4 text-yellow-600" />,
                  approved: <CheckCircle2 className="w-4 h-4 text-green-600" />,
                  rejected: <XCircle className="w-4 h-4 text-destructive" />,
                  processed: <CheckCircle2 className="w-4 h-4 text-blue-600" />,
                  synced: <CheckCircle2 className="w-4 h-4 text-purple-600" />,
                }

                const statusColors = {
                  pending: "bg-yellow-100 text-yellow-800",
                  approved: "bg-green-100 text-green-800",
                  rejected: "bg-red-100 text-destructive",
                  processed: "bg-blue-100 text-blue-800",
                  synced: "bg-purple-100 text-purple-800",
                }

                return (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      {statusIcons[original.status as keyof typeof statusIcons]}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          statusColors[original.status as keyof typeof statusColors] || "bg-foregroundx text-gray-800"
                        }`}
                      >
                        {original.status}
                      </span>
                    </div>
                    {original.rejectReason && (
                      <div className="text-xs text-destructive italic">{original.rejectReason}</div>
                    )}
                  </div>
                )
              },
            },
            {
              Header: "Created",
              accessor: "createdAt",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.createdAt}</div>,
            },
            {
              Header: "Actions",
              accessor: "id",
              Cell: ({ row: { original } }) => (
                <div className="flex gap-2">
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    onClick={() => {
                      // Open detailed view modal or navigate to detail page
                      console.log("View details for:", original.id)
                      alert("View details modal would open here")
                    }}
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              ),
            },
          ]}
          enableSorting
          enablePagination
        />
      </div>
    </div>
  )
}
